/**
 * Dashboard Page - Premium Trading Dashboard
 * Version: 6.2.0
 * 
 * Premium TradingView-style design
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import './Dashboard.css'
import MobileDashboard from '../components/MobileDashboard'
import QuickStartModal from '../components/QuickStartModal'
import { Skeleton, SkeletonStats, SkeletonText } from '../components/SkeletonLoader'
import { LineChart, BarChart, PieChart } from '../components/Charts'
import { CircularProgress, LinearProgress } from '../components/Progress'
import StatCard, { StatGrid, MiniStat } from '../components/StatCard'
import PriceTicker from '../components/PriceTicker'
import OrderBook from '../components/OrderBook'
import TradeTicket from '../components/TradeTicket'
import PortfolioSummary from '../components/PortfolioSummary'

interface Stats {
  totalUsers: number
  activeUsers: number
  exchanges: number
  strategies: number
  brokers: number
  healthStatus: string
  connectedBrokers: number
  runningBots: number
  paperTradingActive: boolean
  liveTradingActive: boolean
}

interface PnLData {
  day: number
  week: number
  month: number
  total: number
}

interface MarketIndex {
  symbol: string
  name: string
  price: number
  change: number
  changePercent: number
}

interface Activity {
  id: string
  type: 'trade' | 'signal' | 'alert' | 'system'
  message: string
  time: string
  icon: string
}

function Dashboard() {
  const navigate = useNavigate()
  const [showQuickStart, setShowQuickStart] = useState(false)
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    activeUsers: 0,
    exchanges: 11,  // Binance, Bybit, Deribit, OKX, Delta, Coinbase, Kraken, KuCoin, Bitget, Gate.io, HTX
    strategies: 0,
    brokers: 82,  // Indian stock brokers
    healthStatus: 'Analyzing...',
    connectedBrokers: 0,
    runningBots: 0,
    paperTradingActive: false,
    liveTradingActive: false
  })
  const [loading, setLoading] = useState(true)
  const [time, setTime] = useState(new Date())
  
  // P&L Data
  const [pnl] = useState<PnLData>({
    day: 2450.50,
    week: 12350.75,
    month: 45600.00,
    total: 234567.89
  })
  
  // Market Indices
  const [indices] = useState<MarketIndex[]>([
    { symbol: 'NIFTY', name: 'Nifty 50', price: 24891.45, change: 125.30, changePercent: 0.51 },
    { symbol: 'SENSEX', name: 'BSE Sensex', price: 82345.67, change: -45.23, changePercent: -0.05 },
    { symbol: 'BANKNIFTY', name: 'Bank Nifty', price: 52145.20, change: 345.60, changePercent: 0.67 },
    { symbol: 'BTC', name: 'Bitcoin', price: 425678.00, change: 1234.50, changePercent: 0.29 },
  ])
  
  // Recent Activity
  const [activities] = useState<Activity[]>([
    { id: '1', type: 'trade', message: 'BUY executed: RELIANCE CE 2500', time: '2 min ago', icon: '📈' },
    { id: '2', type: 'signal', message: 'RSI signal generated for NIFTY', time: '5 min ago', icon: '🔔' },
    { id: '3', type: 'alert', message: 'Strategy EMA Cross strategy triggered', time: '12 min ago', icon: '⚡' },
    { id: '4', type: 'system', message: 'Bot "RSI Strategy" started', time: '15 min ago', icon: '🤖' },
    { id: '5', type: 'trade', message: 'SELL executed: BANKNIFTY FUT', time: '30 min ago', icon: '📉' },
  ])

  useEffect(() => {
    const timer = setTimeout(() => {
      setStats({
        totalUsers: 1,
        activeUsers: 1,
        exchanges: 11,
        strategies: 22,
        brokers: 82,
        healthStatus: 'Optimal',
        connectedBrokers: 2,
        runningBots: 3,
        paperTradingActive: true,
        liveTradingActive: false
      })
      setLoading(false)
    }, 1500)
    return () => clearTimeout(timer)
  }, [])
  
  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000)
    return () => clearInterval(interval)
  }, [])

  if (loading) {
    return (
      <div className="dashboard-loading">
        {/* Header Skeleton */}
        <div className="dashboard-skeleton-header">
          <div className="dashboard-skeleton-header-left">
            <Skeleton width={120} height={14} />
            <Skeleton width={200} height={24} />
          </div>
          <div className="dashboard-skeleton-header-right">
            <Skeleton width={100} height={40} borderRadius={8} />
            <Skeleton width={140} height={40} borderRadius={8} />
          </div>
        </div>

        {/* Market Skeleton */}
        <div className="market-skeleton">
          <div className="market-skeleton-item">
            <Skeleton width={50} height={14} />
            <Skeleton width={80} height={16} />
            <Skeleton width={60} height={14} />
          </div>
          <div className="market-skeleton-item">
            <Skeleton width={50} height={14} />
            <Skeleton width={80} height={16} />
            <Skeleton width={60} height={14} />
          </div>
          <div className="market-skeleton-item">
            <Skeleton width={50} height={14} />
            <Skeleton width={80} height={16} />
            <Skeleton width={60} height={14} />
          </div>
        </div>

        {/* P&L Skeleton */}
        <div className="pnl-skeleton">
          <div className="pnl-skeleton-card">
            <div className="pnl-skeleton-header">
              <Skeleton width={120} height={20} />
              <Skeleton width={80} height={24} borderRadius={20} />
            </div>
            <div className="pnl-skeleton-main">
              <Skeleton width={150} height={40} />
              <Skeleton width={100} height={14} />
            </div>
            <div className="pnl-skeleton-breakdown">
              <Skeleton width="100%" height={40} />
              <Skeleton width="100%" height={40} />
              <Skeleton width="100%" height={40} />
            </div>
          </div>
          <div className="status-mini-skeleton">
            <div className="status-mini-skeleton-item">
              <Skeleton width={40} height={40} borderRadius={10} />
              <div style={{ flex: 1 }}>
                <Skeleton width={30} height={20} />
                <Skeleton width={50} height={12} />
              </div>
            </div>
            <div className="status-mini-skeleton-item">
              <Skeleton width={40} height={40} borderRadius={10} />
              <div style={{ flex: 1 }}>
                <Skeleton width={30} height={20} />
                <Skeleton width={50} height={12} />
              </div>
            </div>
            <div className="status-mini-skeleton-item">
              <Skeleton width={40} height={40} borderRadius={10} />
              <div style={{ flex: 1 }}>
                <Skeleton width={30} height={20} />
                <Skeleton width={50} height={12} />
              </div>
            </div>
            <div className="status-mini-skeleton-item">
              <Skeleton width={40} height={40} borderRadius={10} />
              <div style={{ flex: 1 }}>
                <Skeleton width={30} height={20} />
                <Skeleton width={50} height={12} />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Skeleton */}
        <div className="stats-skeleton">
          <SkeletonStats count={6} />
        </div>
      </div>
    )
  }

  return (
    <div className="dashboard">
      {/* Premium Header */}
      <div className="premium-header">
        <div className="premium-header-left">
          <div className="greeting-section">
            <span className="greeting-time">Good {time.getHours() < 12 ? 'Morning' : time.getHours() < 17 ? 'Afternoon' : 'Evening'}</span>
            <h1 className="greeting-title">Welcome back, Trader</h1>
          </div>
        </div>
        <div className="premium-header-right">
          <div className="market-status">
            <span className="market-label">Market Status</span>
            <span className="market-value">
              <span className="market-dot open" />
              Open
            </span>
          </div>
          <div className="system-time">
            <span className="time-icon">🕐</span>
            <span>{time.toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      {/* Market Overview Bar */}
      <section className="market-overview">
        <div className="market-indices">
          {indices.map((index) => (
            <div key={index.symbol} className="market-index">
              <span className="index-symbol">{index.symbol}</span>
              <span className="index-price">{index.price.toLocaleString()}</span>
              <span className={`index-change ${index.change >= 0 ? 'positive' : 'negative'}`}>
                {index.change >= 0 ? '+' : ''}{index.change.toFixed(2)} ({index.changePercent.toFixed(2)}%)
              </span>
            </div>
          ))}
        </div>
        <button className="view-all-markets" onClick={() => navigate('/analytics')}>
          View All →
        </button>
      </section>

      {/* P&L Widget */}
      <section className="pnl-section">
        <div className="pnl-card">
          <div className="pnl-header">
            <h3>💰 Profit & Loss</h3>
            <span className="pnl-badge">Paper Trading</span>
          </div>
          <div className="pnl-main">
            <span className="pnl-value positive">₹{pnl.day.toLocaleString()}</span>
            <span className="pnl-label">Today's P&L</span>
          </div>
          <div className="pnl-breakdown">
            <div className="pnl-item">
              <span className="pnl-period">This Week</span>
              <span className="pnl-amount positive">+₹{pnl.week.toLocaleString()}</span>
            </div>
            <div className="pnl-item">
              <span className="pnl-period">This Month</span>
              <span className="pnl-amount positive">+₹{pnl.month.toLocaleString()}</span>
            </div>
            <div className="pnl-item">
              <span className="pnl-period">Total</span>
              <span className="pnl-amount positive">+₹{pnl.total.toLocaleString()}</span>
            </div>
          </div>
        </div>
        
        {/* Trading Status Cards */}
        <div className="trading-status-grid">
          <div className={`status-card-mini ${stats.connectedBrokers > 0 ? 'connected' : ''}`}>
            <div className="status-mini-icon">🔗</div>
            <div className="status-mini-content">
              <span className="status-mini-value">{stats.connectedBrokers}</span>
              <span className="status-mini-label">Brokers</span>
            </div>
          </div>
          <div className="status-card-mini running">
            <div className="status-mini-icon">🤖</div>
            <div className="status-mini-content">
              <span className="status-mini-value">{stats.runningBots}</span>
              <span className="status-mini-label">Bots</span>
            </div>
          </div>
          <div className={`status-card-mini ${stats.paperTradingActive ? 'active' : ''}`}>
            <div className="status-mini-icon">📝</div>
            <div className="status-mini-content">
              <span className="status-mini-value">{stats.paperTradingActive ? 'ON' : 'OFF'}</span>
              <span className="status-mini-label">Paper</span>
            </div>
          </div>
          <div className={`status-card-mini ${stats.liveTradingActive ? 'live' : ''}`}>
            <div className="status-mini-icon">🚀</div>
            <div className="status-mini-content">
              <span className="status-mini-value">{stats.liveTradingActive ? 'ON' : 'OFF'}</span>
              <span className="status-mini-label">Live</span>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="stats-section">
        <h2 className="section-title">📊 Platform Statistics</h2>
        <StatGrid columns={4} gap="medium">
          <StatCard
            title="Total Users"
            value="1"
            icon="👥"
            color="primary"
            trend={{ value: 12, direction: 'up', label: 'vs last month' }}
            variant="elevated"
          />
          <StatCard
            title="Active Strategies"
            value="22"
            icon="📈"
            color="success"
            trend={{ value: 8, direction: 'up' }}
          />
          <StatCard
            title="Connected Brokers"
            value={stats.connectedBrokers}
            icon="🔗"
            color="warning"
          />
          <StatCard
            title="Running Bots"
            value={stats.runningBots}
            icon="🤖"
            color="info"
            trend={{ value: 25, direction: 'up', label: 'this week' }}
          />
        </StatGrid>
      </section>

      {/* Performance Charts */}
      <section className="charts-section">
        <h2 className="section-title">📈 Trading Performance</h2>
        <div className="charts-grid">
          <div className="chart-card card">
            <div className="chart-header">
              <h3>Portfolio Growth</h3>
              <span className="chart-badge success">+15.2%</span>
            </div>
            <LineChart 
              data={[
                { label: 'Mon', value: 10000 },
                { label: 'Tue', value: 10500 },
                { label: 'Wed', value: 10200 },
                { label: 'Thu', value: 10800 },
                { label: 'Fri', value: 11520 },
              ]}
              height={200}
              showGrid
              showDots
              showArea
              color="var(--success)"
            />
          </div>
          
          <div className="chart-card card">
            <div className="chart-header">
              <h3>Win Rate by Strategy</h3>
            </div>
            <BarChart 
              data={[
                { label: 'RSI', value: 68, color: 'var(--primary)' },
                { label: 'MACD', value: 72, color: 'var(--success)' },
                { label: 'EMA', value: 65, color: 'var(--warning)' },
                { label: 'BB', value: 70, color: 'var(--info)' },
              ]}
              height={200}
              showValues
            />
          </div>
        </div>
      </section>

      {/* Progress Indicators */}
      <section className="progress-section">
        <h2 className="section-title">⚙️ Trading Status</h2>
        <div className="progress-grid">
          <div className="progress-card card">
            <CircularProgress
              value={75}
              size={120}
              strokeWidth={10}
              label="Today's Goal"
              color="var(--primary)"
            />
          </div>
          <div className="progress-card card">
            <LinearProgress
              value={68}
              height={12}
              showValue
              label="Win Rate"
              variant="gradient"
            />
          </div>
          <div className="progress-card card">
            <MiniStat
              label="Total Trades"
              value="156"
              icon="📊"
              color="var(--success)"
            />
          </div>
          <div className="progress-card card">
            <MiniStat
              label="Avg Profit"
              value="+₹1,250"
              icon="💰"
              color="var(--primary)"
            />
          </div>
        </div>
      </section>

      {/* Trading Section */}
      <section className="trading-section">
        <h2 className="section-title">📈 Live Trading</h2>
        <div className="trading-grid">
          {/* Price Ticker */}
          <div className="trading-card">
            <PriceTicker
              symbol="NIFTY"
              name="Nifty 50"
              initialPrice={24891.45}
              autoUpdate={true}
              showDetails={true}
            />
          </div>
          
          {/* Order Book */}
          <div className="trading-card">
            <OrderBook
              symbol="NIFTY"
              maxLevels={8}
            />
          </div>
          
          {/* Trade Ticket */}
          <div className="trading-card">
            <TradeTicket
              symbol="NIFTY"
              lastPrice={24891.45}
              onSubmit={(order) => console.log('Order submitted:', order)}
            />
          </div>
          
          {/* Portfolio Summary */}
          <div className="trading-card">
            <PortfolioSummary
              totalValue={284500}
              totalPnL={22250}
              totalPnLPercent={8.48}
            />
          </div>
        </div>
      </section>

      {/* Hero Section - Simplified */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="ai-badge">✦ AI Engine Active</span>
          </div>
          <h1 className="hero-title">Neural Trading Dashboard</h1>
          <p className="hero-subtitle">Powered by advanced machine learning algorithms</p>
          <div className="hero-actions">
            <button className="hero-cta primary" onClick={() => setShowQuickStart(true)}>
              <span>🚀</span>
              <span>Start Trading</span>
            </button>
            <button className="hero-cta secondary" onClick={() => navigate('/strategies')}>
              <span>📊</span>
              <span>Explore Strategies</span>
            </button>
          </div>
        </div>
        <div className="hero-orb" />
      </section>

      {/* Quick Actions */}
      <section className="quick-actions-section">
        <h2 className="section-title">⚡ Quick Actions</h2>
        <div className="action-cards">
          <button className="action-card" onClick={() => navigate('/strategies')}>
            <div className="action-icon">📊</div>
            <div className="action-content">
              <span className="action-title">Strategies</span>
              <span className="action-desc">Build & manage strategies</span>
            </div>
            <div className="action-arrow">→</div>
          </button>
          <button className="action-card" onClick={() => navigate('/strategies?tab=backtest')}>
            <div className="action-icon">🔬</div>
            <div className="action-content">
              <span className="action-title">Backtest</span>
              <span className="action-desc">Test on historical data</span>
            </div>
            <div className="action-arrow">→</div>
          </button>
          <button className="action-card" onClick={() => navigate('/strategies?tab=paper')}>
            <div className="action-icon">📝</div>
            <div className="action-content">
              <span className="action-title">Paper Trade</span>
              <span className="action-desc">Practice without risk</span>
            </div>
            <div className="action-arrow">→</div>
          </button>
        </div>
      </section>

      {/* Recent Activity */}
      <section className="activity-section">
        <div className="section-header">
          <h2 className="section-title">📋 Recent Activity</h2>
          <button className="view-all-btn" onClick={() => navigate('/analytics')}>
            View All
          </button>
        </div>
        <div className="activity-feed">
          {activities.map((activity) => (
            <div key={activity.id} className={`activity-item ${activity.type}`}>
              <div className="activity-icon">{activity.icon}</div>
              <div className="activity-content">
                <span className="activity-message">{activity.message}</span>
                <span className="activity-time">{activity.time}</span>
              </div>
              <span className={`activity-indicator ${activity.type}`} />
            </div>
          ))}
        </div>
      </section>

      {/* Backtest Entry Point */}
      <section className="backtest-section">
        <div 
          className="backtest-card" 
          onClick={() => navigate('/strategies?tab=backtest')}
          title="Click to open Backtesting - Test your strategy on historical data before risking real money"
        >
          <div className="backtest-visual">
            <div className="chart-preview">
              <div className="chart-line" />
              <div className="chart-bars">
                <span className="bar" style={{height: '40%'}} />
                <span className="bar" style={{height: '60%'}} />
                <span className="bar" style={{height: '45%'}} />
                <span className="bar" style={{height: '80%'}} />
                <span className="bar" style={{height: '55%'}} />
                <span className="bar" style={{height: '70%'}} />
                <span className="bar" style={{height: '65%'}} />
              </div>
            </div>
            <div className="backtest-badge">New</div>
          </div>
          <div className="backtest-content">
            <div className="backtest-header">
              <span className="backtest-icon">🔬</span>
              <h3>Start Backtesting</h3>
            </div>
            <p className="backtest-description">
              Test your strategy on 5+ years of historical data. Validate before risking real money.
            </p>
            <div className="backtest-stats">
              <div className="backtest-stat">
                <span className="stat-number">500+</span>
                <span className="stat-label">Instruments</span>
              </div>
              <div className="backtest-stat">
                <span className="stat-number">5yr+</span>
                <span className="stat-label">Historical Data</span>
              </div>
              <div className="backtest-stat">
                <span className="stat-number">50+</span>
                <span className="stat-label">Indicators</span>
              </div>
            </div>
            <button className="backtest-cta">
              <span>Launch Backtest</span>
              <span className="arrow">→</span>
            </button>
          </div>
          <div className="backtest-tooltip">
            <span className="tooltip-icon">💡</span>
            <span className="tooltip-text">Validate your strategy on historical data before risking real capital</span>
          </div>
        </div>
      </section>

      {/* Stats Grid - Moved and Enhanced */}
      <section className="stats-section">
        <div className="stats-header">
          <h2 className="section-title">📊 Real-Time Metrics</h2>
          <span className="live-badge">
            <span className="live-dot" />
            LIVE
          </span>
        </div>
        <div className="stats-grid">
          <div className="stat-card card animate-float" style={{ animationDelay: '0s' }}>
            <div className="stat-glow" />
            <div className="stat-icon">👥</div>
            <div className="stat-number">{stats.totalUsers}</div>
            <div className="stat-label">Total Users</div>
            <div className="stat-bar">
              <div className="stat-bar-fill" style={{ width: '100%' }} />
            </div>
          </div>
          <div className="stat-card card animate-float" style={{ animationDelay: '0.1s' }}>
            <div className="stat-glow" />
            <div className="stat-icon">⚡</div>
            <div className="stat-number">{stats.activeUsers}</div>
            <div className="stat-label">Active Sessions</div>
            <div className="stat-bar">
              <div className="stat-bar-fill" style={{ width: '100%' }} />
            </div>
          </div>
          <div className="stat-card card animate-float" style={{ animationDelay: '0.2s' }}>
            <div className="stat-glow" />
            <div className="stat-icon">🌐</div>
            <div className="stat-number">{stats.exchanges}</div>
            <div className="stat-label">Exchanges</div>
            <div className="stat-bar">
              <div className="stat-bar-fill success" style={{ width: '100%' }} />
            </div>
          </div>
          <div className="stat-card card animate-float" style={{ animationDelay: '0.3s' }}>
            <div className="stat-glow" />
            <div className="stat-icon">📈</div>
            <div className="stat-number">{stats.strategies}</div>
            <div className="stat-label">Strategies</div>
            <div className="stat-bar">
              <div className="stat-bar-fill accent" style={{ width: '100%' }} />
            </div>
          </div>
        </div>
      </section>

      {/* System Status */}
      <section className="status-section card">
        <div className="section-header">
          <h2 className="section-title">🔮 System Neural Status</h2>
          <span className={`status-indicator ${stats.healthStatus.toLowerCase()}`}>
            {stats.healthStatus}
          </span>
        </div>
        <div className="status-grid">
          <div className="status-item">
            <div className="status-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
            </div>
            <div className="status-info">
              <span className="status-label">Neural Engine</span>
              <span className="status-value success">Active</span>
            </div>
            <div className="status-bar-mini">
              <div className="bar-fill" style={{ width: '95%' }} />
            </div>
          </div>
          <div className="status-item">
            <div className="status-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </div>
            <div className="status-info">
              <span className="status-label">Broker Adapters</span>
              <span className="status-value">{stats.brokers} Ready</span>
            </div>
            <div className="status-bar-mini">
              <div className="bar-fill success" style={{ width: '100%' }} />
            </div>
          </div>
          <div className="status-item">
            <div className="status-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
              </svg>
            </div>
            <div className="status-info">
              <span className="status-label">Database</span>
              <span className="status-value success">Connected</span>
            </div>
            <div className="status-bar-mini">
              <div className="bar-fill success" style={{ width: '100%' }} />
            </div>
          </div>
          <div className="status-item">
            <div className="status-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div className="status-info">
              <span className="status-label">API Gateway</span>
              <span className="status-value success">Running</span>
            </div>
            <div className="status-bar-mini">
              <div className="bar-fill success" style={{ width: '98%' }} />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="actions-section">
        <h2 className="section-title">⚡ Quick Actions</h2>
        <div className="actions-grid">
          <button className="action-card" onClick={() => navigate('/brokers')}>
            <div className="action-icon">🔗</div>
            <div className="action-content">
              <span className="action-title">Connect Broker</span>
              <span className="action-desc">Link your trading account</span>
            </div>
            <div className="action-arrow">→</div>
          </button>
          <button className="action-card" onClick={() => navigate('/strategies')}>
            <div className="action-icon">📊</div>
            <div className="action-content">
              <span className="action-title">Create Strategy</span>
              <span className="action-desc">Build your trading strategy</span>
            </div>
            <div className="action-arrow">→</div>
          </button>
          <button className="action-card" onClick={() => navigate('/strategies?tab=backtest')}>
            <div className="action-icon">🔬</div>
            <div className="action-content">
              <span className="action-title">Backtest</span>
              <span className="action-desc">Test on historical data</span>
            </div>
            <div className="action-arrow">→</div>
          </button>
          <button className="action-card" onClick={() => navigate('/strategies?tab=paper')}>
            <div className="action-icon">📝</div>
            <div className="action-content">
              <span className="action-title">Paper Trade</span>
              <span className="action-desc">Practice without risk</span>
            </div>
            <div className="action-arrow">→</div>
          </button>
        </div>
      </section>

      {/* Trading Status Section */}
      <section className="trading-status-section">
        <h2 className="section-title">📈 Trading Status</h2>
        <div className="status-cards">
          <div className={`status-card ${stats.connectedBrokers > 0 ? 'connected' : 'disconnected'}`}>
            <div className="status-card-icon">🔗</div>
            <div className="status-card-content">
              <span className="status-card-title">Brokers</span>
              <span className="status-card-value">{stats.connectedBrokers} Connected</span>
            </div>
            <span className={`status-dot ${stats.connectedBrokers > 0 ? 'active' : ''}`} />
          </div>
          <div className="status-card running">
            <div className="status-card-icon">🤖</div>
            <div className="status-card-content">
              <span className="status-card-title">Running Bots</span>
              <span className="status-card-value">{stats.runningBots} Active</span>
            </div>
            <span className="status-dot active pulse" />
          </div>
          <div className={`status-card ${stats.paperTradingActive ? 'active' : ''}`}>
            <div className="status-card-icon">📝</div>
            <div className="status-card-content">
              <span className="status-card-title">Paper Trading</span>
              <span className="status-card-value">{stats.paperTradingActive ? 'Active' : 'Inactive'}</span>
            </div>
            <span className={`status-dot ${stats.paperTradingActive ? 'active' : ''}`} />
          </div>
          <div className={`status-card ${stats.liveTradingActive ? 'live' : ''}`}>
            <div className="status-card-icon">🚀</div>
            <div className="status-card-content">
              <span className="status-card-title">Live Trading</span>
              <span className="status-card-value">{stats.liveTradingActive ? 'Active' : 'Inactive'}</span>
            </div>
            <span className={`status-dot ${stats.liveTradingActive ? 'live' : ''}`} />
          </div>
        </div>
      </section>

      {/* Backtest Entry Point */}
      <section className="backtest-section">
        <div 
          className="backtest-card" 
          onClick={() => navigate('/strategies?tab=backtest')}
          title="Click to open Backtesting - Test your strategy on historical data before risking real money"
        >
          <div className="backtest-visual">
            <div className="chart-preview">
              <div className="chart-line" />
              <div className="chart-bars">
                <span className="bar" style={{height: '40%'}} />
                <span className="bar" style={{height: '60%'}} />
                <span className="bar" style={{height: '45%'}} />
                <span className="bar" style={{height: '80%'}} />
                <span className="bar" style={{height: '55%'}} />
                <span className="bar" style={{height: '70%'}} />
                <span className="bar" style={{height: '65%'}} />
              </div>
            </div>
            <div className="backtest-badge">New</div>
          </div>
          <div className="backtest-content">
            <div className="backtest-header">
              <span className="backtest-icon">🔬</span>
              <h3>Start Backtesting</h3>
            </div>
            <p className="backtest-description">
              Test your strategy on 5+ years of historical data. Validate before risking real money.
            </p>
            <div className="backtest-stats">
              <div className="backtest-stat">
                <span className="stat-number">500+</span>
                <span className="stat-label">Instruments</span>
              </div>
              <div className="backtest-stat">
                <span className="stat-number">5yr+</span>
                <span className="stat-label">Historical Data</span>
              </div>
              <div className="backtest-stat">
                <span className="stat-number">50+</span>
                <span className="stat-label">Indicators</span>
              </div>
            </div>
            <button className="backtest-cta">
              <span>Launch Backtest</span>
              <span className="arrow">→</span>
            </button>
          </div>
          <div className="backtest-tooltip">
            <span className="tooltip-icon">💡</span>
            <span className="tooltip-text">Validate your strategy on historical data before risking real capital</span>
          </div>
        </div>
      </section>

      {/* Broker Network */}
      <section className="network-section card">
        <div className="section-header">
          <h2 className="section-title">🔗 Broker Neural Network</h2>
          <span className="network-count">{stats.brokers} Adapters</span>
        </div>
        <div className="network-visual">
          <div className="network-core">
            <div className="core-ring ring-1" />
            <div className="core-ring ring-2" />
            <div className="core-ring ring-3" />
            <div className="core-center">🚀</div>
          </div>
          <div className="network-stats">
            <div className="network-stat">
              <span className="stat-value">82</span>
              <span className="stat-label">Total Brokers</span>
            </div>
            <div className="network-stat">
              <span className="stat-value">8</span>
              <span className="stat-label">Major Indian</span>
            </div>
            <div className="network-stat">
              <span className="stat-value">20</span>
              <span className="stat-label">Full Variants</span>
            </div>
            <div className="network-stat">
              <span className="stat-value">54</span>
              <span className="stat-label">Regional</span>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Dashboard */}
      <div className="mobile-dashboard-container">
        <MobileDashboard />
      </div>

      {/* Quick Start Modal */}
      <QuickStartModal 
        isOpen={showQuickStart} 
        onClose={() => setShowQuickStart(false)} 
      />
    </div>
  )
}

export default Dashboard